/* eslint-disable @typescript-eslint/no-unused-vars */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import cn from 'classnames';
import {
  clone,
  defaults,
  get,
  hasIn,
  isFunction,
  isPlainObject,
  setWith,
  unset,
  updateWith,
} from 'lodash';
import React, {
  Component,
  Fragment,
  Suspense,
  useEffect,
  useState,
} from 'react';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { animateScroll } from 'react-scroll';
import { ToastContainer } from 'react-toastify';
import Alert from './components/Alert';
import { DesktopAppHeader } from './components/DesktopAppHeader';
import { MobileAppHeader } from './components/MobileAppHeader';
import ModalStackProvider from './components/modal/ModalStackProvider';
import NPSSurveyContextProvider from './components/nps/NPSSurveyContextProvider';
import ProtectedRoute from './components/ProtectedRoute';
import { PurchaseCTAProvider } from './components/PurchaseCTA';
import {
  api,
  AppContext,
  authSignIn,
  authSignUp,
  clerkLogin,
  getAppConfig,
  getAuthState,
  initialize,
  useApp,
  withAppContext,
} from './helpers/api-helpers';

import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import {
  useGateValue,
  useStatsigClient,
  useStatsigUser,
} from '@statsig/react-bindings';

import { UpscopeProvider } from '@upscopeio/react';
import { Product } from './App.interfaces';
import { GlobalNavbar } from './components/GlobalNavbar';
import { withStatsigClient } from './helpers/stat-helpers';
import { ReactComponent as AdminIcon } from './icons/admin.svg';
import { ReactComponent as CoursesIcon } from './icons/courses.svg';
import { ReactComponent as JobsIcon } from './icons/jobs.svg';
import { ReactComponent as OrgsIcon } from './icons/orgs.svg';
import { OrganizationProvider } from './pages/organizations/v4/OrganizationContext/OrganizationContext';

const Rollbar = window.Rollbar;
const Pendo = window.pendo;
const SignIn = React.lazy(() => import('./pages/SignIn'));
const AcceptInvite = React.lazy(() => import('./pages/AcceptInvite'));
const OrganizationDashboardV3 = React.lazy(
  () => import('./pages/organizations/v3/OrganizationDashboard'),
);
const OrganizationDashboard = React.lazy(
  () =>
    import(
      './pages/organizations/v4/OrganizationDashboard/OrganizationDashboard'
    ),
);
const JobsTitle = withAppContext((props) => {
  const {
    app: { user },
  } = props;

  return <Fragment>Teaching Jobs</Fragment>;
});

function Logout() {
  const { signOut, authState } = useApp();

  useEffect(() => {
    if (authState === 'signedIn') {
      signOut();
    }
  }, [signOut, authState]);

  return authState === 'signIn' ? <Redirect to="/" /> : null;
}

const pages = [
  {
    title: 'Courses',
    path: '/courses',
    icon: <CoursesIcon />,
    requireAuth: true,
    showTab: true,
    component: React.lazy(() => import('./pages/Courses')),
  },
  {
    title: <JobsTitle />,
    path: '/jobs',
    icon: <JobsIcon />,
    requireAuth: true,
    showTab: ({ flags }) => flags?.hasJobsAccess || false,
    component: React.lazy(() => import('./pages/jobs/Jobs')),
  },
  {
    title: 'Profile',
    path: '/profile',
    requireAuth: true,
    component: React.lazy(() => import('./pages/profile/Profile')),
  },
  {
    title: 'Organization',
    path: '/organizations/v3',
    icon: <OrgsIcon />,
    requireAuth: true,
    showTab: ({ user, flags }) =>
      !flags?.enableRedesignedEppDashboard &&
      user &&
      user.memberships.filter((m) => ['admin', 'observer'].includes(m.role))
        .length > 0,
    component: OrganizationDashboardV3,
  },
  {
    title: 'Organization',
    path: '/organizations',
    icon: <OrgsIcon />,
    requireAuth: true,
    showTab: ({ user, flags }) =>
      flags?.enableRedesignedEppDashboard &&
      user &&
      user.memberships.filter((m) => ['admin', 'observer'].includes(m.role))
        .length > 0,
    component: withAppContext(({ app: { flags } }) => {
      if (!flags?.enableRedesignedEppDashboard) return null;
      return (
        <OrganizationProvider>
          <OrganizationDashboard />
        </OrganizationProvider>
      );
    }),
  },
  {
    title: 'Admin',
    path: '/admin',
    icon: <AdminIcon />,
    requireAuth: true,
    showTab: ({ user }) => user && user.groups && user.groups.includes('admin'),
    authorizedGroups: ['admin'],
    component: React.lazy(() => import('./pages/admin/Admin')),
  },
  {
    path: '/grading-requests/:gradingRequestId/prompt',
    requireAuth: true,
    component: React.lazy(() => import('./pages/GradingRequestPromptViewer')),
  },
  {
    path: '/grading-requests/:gradingRequestId',
    requireAuth: true,
    component: React.lazy(() => import('./pages/GradingRequestEditor')),
  },
  {
    path: '/subscribe/:product/:key',
    requireAuth: false,
    component: React.lazy(() => import('./pages/subscribe/SubscribeGate')),
  },
  {
    path: '/subscribe/:product',
    requireAuth: false,
    component: React.lazy(() => import('./pages/subscribe/SubscribeGate')),
  },
  {
    path: '/password/reset/:code',
    requireAuth: false,
    component: withAppContext(
      ({
        app: { user, authState },
        history,
        match: {
          params: { code },
        },
      }) => {
        if (authState === 'loading') {
          return null;
        }

        if (user) {
          return <Redirect to="/" />;
        }

        return (
          <SignIn
            defaultParams={{ emailCode: code }}
            onSignIn={() => history.replace('/')}
          />
        );
      },
    ),
  },
  {
    path: '/invite/:token',
    requireAuth: false,
    component: (props) => <AcceptInvite token={props.match.params.token} />,
  },
  { path: '/logout', requireAuth: false, component: Logout },
  {
    path: '/preview/:productId/courses',
    requireAuth: false,
    component: React.lazy(() => import('./pages/ProductCoursesPreview')),
  },
  {
    path: '/preview/:product/:course',
    requireAuth: false,
    component: withStatsigClient((props) => {
      const {
        match: {
          params: { product, course },
        },
        /** @type {import('./helpers/stat-helpers').StatsigClientType | undefined} */
        statsigClient,
      } = props;
      const { logEvent } = statsigClient.client;

      logEvent('preview_course_landing', 1, {
        course_id: course,
        product_id: product,
        source: 'Marketing Website',
      });

      return (
        <Redirect to={`/preview/${product}/courses/${course}/dashboard`} />
      );
    }),
  },
  {
    path: '/preview/:previewId',
    requireAuth: false,
    component: React.lazy(() => import('./pages/ProductCoursesPreview')),
  },
];

function AppHeader(props) {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const value = useGateValue('enable_product_bar');

  return (
    <div className="main-header">
      {value && <GlobalNavbar selectedSite="prep" />}

      {width < 700 ? <MobileAppHeader /> : <DesktopAppHeader />}
    </div>
  );
}

/**
 * This function only needs to run once in the lifecycle of a user.
 * It wil onboard a user (by adding certain metadata) to Clerk.
 * See the `clerkOnboardingRequired` for what sort of information is needed
 * to consider a user onboarded.
 * @param {*} clerkUser
 * @param {*} clerkAuth
 */
async function onboardClerkUser(clerkUser, clerkAuth) {
  const getTokenFn = async () => {
    return clerkAuth.getToken();
  };
  await initialize(getTokenFn);

  const id = clerkUser.user.id;
  return (clientOnboardingPromise = api.users
    .clerkOnboarding({ id })
    .then(({ success }) => {
      clientOnboardingPromise = null;
      // This reload will force the user and session tokens to be refreshed
      // since after this call there is new metadata on the user.
      location.reload();
    })
    .catch((error) => {
      console.error('Onboarding failed', error);
      clientOnboardingPromise = null;
    }));
}

let clientOnboardingPromise = null;

function clerkOnboardingRequired(clerkUser) {
  return (
    !clientOnboardingPromise &&
    clerkUser?.user?.id &&
    !clerkUser?.user?.publicMetadata?.cognitoId
  );
}

// Taken from here https://stackoverflow.com/questions/53371356/how-can-i-use-react-hooks-in-react-classic-class-component
function withHooks(Component) {
  return function WrappedComponent(props) {
    const [attemptingOnboarding, setAttemptingOnboarding] = useState(false);

    const statSigUser = useStatsigUser();
    const clerkUser = useUser();
    const clerkAuth = useAuth();
    const clerk = useClerk();
    const ss = useStatsigClient();
    const getGates = () => {
      return {
        isOrganizationReportsTabEnabled: ss.checkGate(
          'is-organization-reports-tab-enabled',
        ),
        hasJobsAccess: ss.checkGate('has-jobs-access'),
        enableRedesignedEppDashboard: ss.checkGate(
          'enable-redesigned-epp-dashboard',
        ),
        couponCodeField: ss.checkGate('coupon-code-field'),
        enableUpscope: ss.checkGate('enable-upscope'),
        canGenerateEppConceptScoresReport: ss.checkGate(
          'can-generate-epp-concept-scores-report',
        ),
        careerVaultApiAccessDisabled: ss.checkGate(
          'career-vault-api-access-disabled',
        ),
        unlockPracticeTests: ss.checkGate('unlock-practice-tests'),
      };
    };

    useEffect(() => {
      if (!attemptingOnboarding && clerkOnboardingRequired(clerkUser)) {
        setAttemptingOnboarding(true);
        console.warn('Clerk User need to be onboarded.');
        onboardClerkUser(clerkUser, clerkAuth);
      }
    }, [attemptingOnboarding, clerkUser]);

    if (clerkOnboardingRequired(clerkUser)) {
      return <div></div>;
    }

    return (
      <Component
        {...props}
        statSigUser={statSigUser}
        clerkUser={clerkUser}
        clerkAuth={clerkAuth}
        clerk={clerk}
        flags={getGates()}
      />
    );
  };
}

/**
 * Generates the checkout path URL for a given product with optional query parameters.
 * @param product - Either a `Product` object or a product ID. If a `Product` object is provided, the function will use its `id` property.
 * @param queryParams - Optional query parameters that will be appended to the URL.
 * @returns A string representing the checkout path URL.
 */
export function getProductCheckoutPath(
  product: Product | string,
  queryParams?: Record<string, string>,
): string {
  const searchQuery =
    queryParams &&
    URLSearchParams &&
    `?${new URLSearchParams(queryParams).toString()}`;
  return `/subscribe/${product.id || product}${searchQuery || ''}`;
}

const App = withHooks(
  withRouter(
    class extends Component {
      state = {
        authState: 'loading',
        error: null,
        user: null,
        appConfig: {},
        uiState: {},
        modals: [],
        isReseting: false,
      };

      mainContentRef = React.createRef();

      componentDidMount = () => {
        const {
          location: { pathname },
        } = this.props;
        const autologin = !/^\/logout\/?/.test(pathname);
        this.reset(autologin);
        Pendo.initialize();
      };

      componentDidUpdate = (_prevProps, prevState) => {
        const oldUserId = prevState.user && prevState.user.id;
        const { user } = this.state || {};

        if (
          this.props.clerkUser.isSignedIn !== _prevProps.clerkUser.isSignedIn
        ) {
          this.reset(true);
        }

        if (
          oldUserId !== user?.id ||
          prevState.isReseting !== this.state.isReseting ||
          this.state.authState !== prevState.authState
        ) {
          Rollbar.configure({
            payload: { person: { id: user?.id || 'unknown' } },
          });

          if (user) {
            const productNames = user.subscriptions
              ? user.subscriptions.map(
                  (subscription) => subscription?.product?.name,
                )
              : [];

            const userInfo = {
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              live: user.live,
              productNames,
              orgs: user.memberships.map((m) => m.org.name).flat(),
            };

            const userLike = user && {
              kind: 'user',
              key: user?.id,
              ...userInfo,
            };

            //only identify for Pendo if we have a user.id
            if (user?.id) {
              const userPendo = { id: user.id, ...userInfo };
              Pendo.identify({ visitor: userPendo });
            }

            try {
              this.props.statSigUser.updateUserAsync({
                userID: user.id,
                email: user.email,
                customIDs: { emailId: user.email.toLocaleLowerCase() },
                custom: { ...userInfo },
              });
            } catch (e) {
              console.error('Failed to update user in Statsig', e);
            }
          }
        }
      };

      shouldComponentUpdate = (nextProps, nextState) => {
        return !nextState.isReseting;
      };

      reset = async (autologin = true, url) => {
        const { history } = this.props;

        this.setState({ error: null, isReseting: true });
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const app = this;
        try {
          const getTokenFn = async () => {
            if (!app.props.clerkUser.user?.publicMetadata?.cognitoId) {
              return null;
            }
            return app.props.clerkAuth.getToken();
          };
          await initialize(getTokenFn);
          this.setState({
            uiState: {},
            authState: autologin ? 'loading' : 'signIn',
            user: null,
            appConfig: getAppConfig(),
          });
        } catch (err) {
          console.error(err);
          const error =
            'Uh oh, we are experiencing an issue. Please try again later.';
          this.setState({
            error,
            uiState: {},
            authState: 'error',
            user: null,
            isReseting: false,
          });
          return;
        }

        if (autologin) {
          try {
            if (
              this.props.clerkUser.isSignedIn &&
              this.props.clerkUser.isLoaded
            ) {
              const userFromTf = await clerkLogin();
              console.log('clerkLogin', userFromTf);

              const clerkUser = {
                groups:
                  this.props.clerkUser.user?.publicMetadata['cognitoGroups'],
                ...userFromTf,
                email:
                  this.props.clerkUser.user?.primaryEmailAddress.emailAddress,
                firstName: this.props.clerkUser.user?.firstName,
                lastName: this.props.clerkUser.user?.lastName,
              };

              this.setState({ authState: 'signedIn', user: clerkUser });
            } else if (this.props.clerkUser.isLoaded) {
              this.setState({ authState: 'signIn', user: null });
            }
          } catch (err) {
            console.error(err);
            const error = 'Login Error';
            this.setState({
              error,
              authState: 'signIn',
              user: null,
              isReseting: false,
            });
          }
        }

        if (url) {
          history.replace(url);
        }

        this.setState({ isReseting: false });
      };

      signUp = async (params) => {
        const authParams = {
          username: params.username,
          password: params.password,
          attributes: {
            email: params.username,
            given_name: params.firstName,
            family_name: params.lastName,
          },
        };

        await authSignUp(authParams);
        await this.signIn(authParams.username, authParams.password);
      };

      signIn = async (username, password) => {
        await authSignIn(username, password);
        await this.reset();
      };

      signOut = async () => {
        this.props.clerk.signOut({ redirect: '/' });
        this.setState({ uiState: {}, authState: getAuthState(), user: null });
      };

      saveUIState = (path, value, callback) => {
        this.setState((state) => {
          const uiState = updateWith(
            clone(state.uiState),
            path,
            (oldValue) => {
              const newValue = isFunction(value) ? value(oldValue) : value;
              return !isPlainObject(oldValue)
                ? newValue
                : { ...oldValue, ...newValue };
            },
            clone,
          );
          return { uiState };
        }, callback);
      };

      loadUIState = (path) => {
        return get(this.state.uiState, path);
      };

      wipeUIState = (path, callback) => {
        this.setState((state) => {
          if (hasIn(state.uiState, path)) {
            // Create an immutable copy, with the path value cleared
            const uiState = unset(
              setWith(clone(state.uiState), path, undefined, clone),
              path,
            );
            return { uiState };
          }
        }, callback);
      };

      handleUpdateUser = (user) => {
        if (this.state.user?.id && this.state.user.id === user?.id) {
          user = {
            ...this.state.user, // Start with the previous values
            ...user, // Override with any new values from the update
            groups: this.state.user.groups, // Groups come from auth token
          };

          this.setState({ user });
        }
      };

      openProductCheckout = (
        product: Product | string,
        queryParams?: Record<string | string>,
      ) => {
        this.props.history.push(getProductCheckoutPath(product, queryParams));
      };

      render = () => {
        const { appConfig, user, authState, error } = this.state;
        const {
          history,
          flags,
          location: { pathname },
        } = this.props;

        const tabs = pages.filter(
          ({ showTab }) =>
            showTab === true ||
            (isFunction(showTab) && showTab({ user, flags })),
        );

        // Select the default page a user should see when they first log in
        // This can be modified by the users role in the future
        const selectDefaultPage = () => {
          const defaultPage = tabs.find((tab) => tab.path === '/courses');
          return defaultPage || tabs[0];
        };

        const appContext = {
          authState,
          user,
          clerk: this.props.clerk,
          clerkUser: this.props.clerkUser,
          tabs,
          config: appConfig,
          flags,
          signUp: this.signUp,
          signIn: this.signIn,
          signOut: this.signOut,
          reset: this.reset,
          loadUIState: this.loadUIState,
          saveUIState: this.saveUIState,
          wipeUIState: this.wipeUIState,
          onUpdateUser: this.handleUpdateUser,
          openCourseDashboard: (course) => {
            history.push(`/courses/${course.id || course}/dashboard`);
          },
          openCourseCatalog: () => {
            history.push('/courses');
          },
          openProductCheckout: this.openProductCheckout,
          getCourseDashboardUrl: (course) =>
            `/courses/${course.id || course}/dashboard`,
          getOrganizationDashboardUrl: (org) =>
            `/organizations/${org.id || org}/monitor`,
          dom: { mainContent: this.mainContentRef.current },
          scrollToTop: (options) => {
            options = defaults(options, {
              duration: 250,
              container: this.mainContentRef.current,
            });

            animateScroll.scrollToTop(options);
          },
        };
        if (!this.props.clerkUser.isLoaded) {
          return <div></div>;
        }

        return (
          <AppContext.Provider value={appContext}>
            <UpscopeProvider
              apiKey="GfR4Z2JiUd"
              enabled={!!flags?.enableUpscope}
              uniqueId={user?.email}
            >
              <PurchaseCTAProvider>
                <ModalStackProvider>
                  <NPSSurveyContextProvider>
                    <div className="app">
                      <AppHeader />
                      <div
                        className={cn('main-content', {
                          'white-bg': pathname.includes('/subscribe/'),
                        })}
                        id="content"
                        ref={this.mainContentRef}
                      >
                        <Suspense fallback={null}>
                          {error && (
                            <Alert
                              theme={{ errorAlert: 'error' }}
                              message={error}
                            />
                          )}
                          <Switch>
                            {pages.map((page) => (
                              <ProtectedRoute
                                key={page.path}
                                path={page.path}
                                authorizedGroups={page.authorizedGroups}
                                component={page.component}
                                unauthenticated={SignIn}
                                requireAuth={page.requireAuth}
                              />
                            ))}
                            <Route
                              render={() => (
                                <Redirect to={selectDefaultPage().path} />
                              )}
                            />
                          </Switch>
                        </Suspense>
                      </div>
                      <Suspense fallback={null}>
                        <ToastContainer
                          newestOnTop
                          toastClassName="toast"
                          progressClassName="toast-progress"
                          hideProgressBar
                          closeButton={false}
                          closeOnClick={true}
                          position={'top-center'}
                        />
                      </Suspense>
                    </div>
                  </NPSSurveyContextProvider>
                </ModalStackProvider>
              </PurchaseCTAProvider>
            </UpscopeProvider>
          </AppContext.Provider>
        );
      };
    },
  ),
);

export default App;
